import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { flatten } from 'lodash'
import { useXp } from './XpContext'
import { getBoundsFromCoords } from '../utils/helpers/map.helpers'
import {
  FIT_BOUNDS_ANIMATION_DURATION,
  FIT_BOUNDS_PADDING,
  ROUTE_STYLE_OPTS,
} from '../utils/constants/map.constants'

interface PositionData {
  radius: number
  deadband: number
  label?: string
  description?: string
  lat: number
  lon: number
}

const ROUTE_OPACITY_TIMEOUT = 3000

interface MapControlsContextType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapRef: React.RefObject<any>
  route: number[][]
  resetRoute: () => void
  setRouteSegment: (segment: { longitude: number; latitude: number }) => void
  routeOpacity: number
  fitMapBounds: () => void
  dragging: boolean
  setDragging: (dragging: boolean) => void
  drawer: boolean
  setDrawer: (drawer: boolean) => void
  drawerForced: boolean
  setDrawerForced: (drawerForced: boolean) => void
}

const MapControlsContext = createContext<MapControlsContextType>({
  mapRef: { current: null },
  route: [],
  resetRoute: () => {},
  setRouteSegment: () => {},
  routeOpacity: ROUTE_STYLE_OPTS.opacity,
  fitMapBounds: () => {},
  dragging: false,
  setDragging: () => {},
  drawer: false,
  setDrawer: () => {},
  drawerForced: false,
  setDrawerForced: () => {},
})

const useMapControls = () => useContext(MapControlsContext)

interface MapControlsContextProviderProps {
  children: React.ReactNode
}

const MapControlsContextProvider: React.FC<MapControlsContextProviderProps> = ({ children }) => {
  const { location, positions, manualMode } = useXp()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null)

  const [dragging, setDragging] = useState(false)

  const [route, setRoute] = useState<number[][]>([])
  const [routeOpacity, setRouteOpacity] = useState(ROUTE_STYLE_OPTS.opacity)
  const segmentOpacityTimeout = useRef<NodeJS.Timeout | null>(null)

  const { experienceId } = useParams()

  const [drawer, setDrawer] = useState(false)
  const [drawerForced, setDrawerForced] = useState(!experienceId)

  const fitMapBounds = useCallback(() => {
    if (!mapRef.current || !positions || !Array.isArray(positions) || positions.length === 0) {
      return
    }

    const map = mapRef.current
    const mapBounds = getBoundsFromCoords(
      flatten(positions.map((pattern) => pattern.positions)).map((p: PositionData) => [p.lon, p.lat])
    )
    map.fitBounds(mapBounds, {
      padding: FIT_BOUNDS_PADDING,
      duration: FIT_BOUNDS_ANIMATION_DURATION,
    })
  }, [mapRef.current, positions])

  const resetRoute = useCallback(() => {
    setRoute([])
  }, [])

  const setRouteSegment = useCallback(
    (segment: { longitude: number; latitude: number }) => {
      setRouteOpacity(ROUTE_STYLE_OPTS.opacity)
      if (segmentOpacityTimeout.current) {
        clearTimeout(segmentOpacityTimeout.current)
      }
      segmentOpacityTimeout.current = setTimeout(() => setRouteOpacity(0), ROUTE_OPACITY_TIMEOUT)

      setRoute((prev) => {
        // keep only last two
        const paths = prev.slice(-2)
        return [...paths, [segment.longitude, segment.latitude]]
      })
    },
    [setRoute]
  )

  useEffect(() => {
    if (!location?.coords) return
    setRouteSegment({
      longitude: location.coords.longitude,
      latitude: location.coords.latitude,
    })
  }, [location])

  useEffect(() => resetRoute(), [manualMode])

  const contextValue = useMemo(
    () => ({
      mapRef,
      route,
      setRouteSegment,
      routeOpacity,
      resetRoute,
      fitMapBounds,
      dragging,
      setDragging,
      drawer,
      setDrawer,
      drawerForced,
      setDrawerForced,
    }),
    [mapRef, route, routeOpacity, resetRoute, fitMapBounds, dragging, drawer, drawerForced]
  )

  return <MapControlsContext.Provider value={contextValue}>{children}</MapControlsContext.Provider>
}

export { useMapControls, MapControlsContextProvider }
